import { useCallback } from "react";

import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

import { CheckIcon, Trash2Icon } from "@lucide/icons";

import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";

import api from "api";
import { useUserReload } from "utils/useUserReload";

import type {
  InvitationAcceptResponse,
  NotificationContentProps,
} from "../../types";

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const InvitedActions = ({
  events = [],
  invitations = [],
  onAccept,
  onReject,
}: NotificationContentProps) => {
  const navigate = useNavigate();
  const invitationId = events[0]?.invitation;
  const invitationExists = invitationId
    ? invitations.find((i) => i._id === invitationId && !i.acceptedAt)
    : undefined;
  const reloadUser = useUserReload();

  const acceptInvite = useCallback(() => {
    if (!invitationId) return;
    api.invitations.accept(
      invitationId,
      (data: InvitationAcceptResponse) => {
        onAccept?.(invitationId);
        if (data.group) {
          const { group } = data;
          reloadUser(() => {
            api.invalidateQueries(api.groups.get, [group._id]);
            api.invalidateQueries(api.pages.getAll, ["group", group._id]);
            api.invalidateQueries(api.discussions.getAll, ["group", group._id]);
            navigate(`/groups/${group._id}`);
          });
        }
        toast.success("Group joined");
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, [invitationId, onAccept, navigate, reloadUser]);

  const rejectInvite = useCallback(() => {
    if (!invitationId) return;
    api.invitations.reject(
      invitationId,
      () => {
        onReject?.(invitationId);
        toast.success("Group invitation rejected");
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, [invitationId, onReject]);

  if (!invitationExists) return null;
  return (
    <ButtonContainer>
      <Button
        color="green"
        size="small"
        onClick={acceptInvite}
        style={{ marginBottom: 5 }}
      >
        <Icon icon={CheckIcon} />
      </Button>
      <Button size="small" onClick={rejectInvite} variant="outline">
        <Icon icon={Trash2Icon} />
      </Button>
    </ButtonContainer>
  );
};

export default InvitedActions;
